// react-map-gl v7 wrapper around @mapbox/mapbox-gl-draw.
// Adds an interactive polygon drawing/editing control to a <Map>. Drop it in as
// a child of <Map> (it registers itself via useControl). Callbacks fire on
// create/update/delete with the raw mapbox-gl-draw event (e.features = GeoJSON).
// `onReady(drawInstance)` hands back the MapboxDraw instance so the parent can
// load/clear/change-mode imperatively.
//
// Freehand: pass `freehand` to register mapbox-gl-draw-freehand-mode under the
// `draw_polygon` mode, so press-drag-release traces a boundary like a pencil and
// auto-closes on release (instead of click-per-vertex).
//
// Note: in react-map-gl v7, useControl's onAdd/onRemove callbacks receive only
// the map context ({ map }); the control instance is the factory's return value.
// We capture it in a ref so the add/remove handlers can reference it.
import { useRef } from 'react'
import { useControl } from 'react-map-gl'
import MapboxDraw from '@mapbox/mapbox-gl-draw'
import * as FreehandModeModule from 'mapbox-gl-draw-freehand-mode'
import '@mapbox/mapbox-gl-draw/dist/mapbox-gl-draw.css'

// The package exports the draw-mode object as its default; handle CJS/ESM interop.
const FreehandMode = FreehandModeModule.default || FreehandModeModule

export default function DrawControl(props) {
  const {
    position = 'top-left',
    freehand = false,
    onCreate,
    onUpdate,
    onDelete,
    onReady,
    ...drawOptions
  } = props

  const drawInstanceRef = useRef(null)

  useControl(
    () => {
      // When freehand is on, override the draw_polygon mode with the freehand
      // pencil mode so press-drag-release traces and closes the boundary.
      const modes = freehand
        ? { ...MapboxDraw.modes, draw_polygon: FreehandMode }
        : MapboxDraw.modes
      const draw = new MapboxDraw({ ...drawOptions, modes })
      drawInstanceRef.current = draw
      return draw
    },
    ({ map }) => {
      if (onCreate) map.on('draw.create', onCreate)
      if (onUpdate) map.on('draw.update', onUpdate)
      if (onDelete) map.on('draw.delete', onDelete)
      // Freehand draws by dragging — disable the map's drag-pan while the draw
      // control is active so the drag traces the boundary instead of panning the
      // map. Re-enabled on remove (when drawing ends / control unmounts).
      if (freehand && map.dragPan) map.dragPan.disable()
      if (onReady) onReady(drawInstanceRef.current)
    },
    ({ map }) => {
      if (onCreate) map.off('draw.create', onCreate)
      if (onUpdate) map.off('draw.update', onUpdate)
      if (onDelete) map.off('draw.delete', onDelete)
      if (freehand && map.dragPan) map.dragPan.enable()
      if (onReady) onReady(null)
    },
    { position }
  )

  return null
}